import type {
  DebugLinkedInMessage,
  DebugLinkedInMessageAuthor,
  DebugLinkedInSenderKind,
  DebugLinkedInThread,
} from './types';

type SearchableThread = Pick<DebugLinkedInThread, 'candidateName'> & {
  candidateJobs: Pick<
    DebugLinkedInThread['candidateJobs'][number],
    'jobTitle'
  >[];
};

function normalize(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '');
}

/** Threads whose candidate name or any job title contains the query, ignoring case and diacritics. */
export function filterThreads<T extends SearchableThread>(
  threads: T[],
  query: string,
): T[] {
  const needle = normalize(query.trim());
  if (!needle) return threads;
  return threads.filter(
    (thread) =>
      normalize(thread.candidateName).includes(needle) ||
      thread.candidateJobs.some((job) =>
        normalize(job.jobTitle).includes(needle),
      ),
  );
}

export type MessageStateKind =
  | 'scheduled'
  | 'queued'
  | 'draft'
  | 'failed'
  | 'sent';

/** What a message bubble shows: its real status and the time that status refers to. */
export function messageState(
  message: Pick<
    DebugLinkedInMessage,
    'status' | 'createdAt' | 'sentAt' | 'scheduledAt'
  >,
): { kind: MessageStateKind; at: string } {
  switch (message.status) {
    case 'Pending':
      return message.scheduledAt
        ? { kind: 'scheduled', at: message.scheduledAt }
        : { kind: 'queued', at: message.createdAt };
    case 'Draft':
      return { kind: 'draft', at: message.createdAt };
    case 'Failed':
      return { kind: 'failed', at: message.createdAt };
    default:
      return { kind: 'sent', at: message.sentAt ?? message.createdAt };
  }
}

const SENDER_KIND_LABELS: Record<DebugLinkedInSenderKind, string> = {
  SelfService: 'HM (self-service)',
  InternalRecruiter: 'Internal recruiter',
  Recruiter: 'Recruiter',
};

export function senderKindLabel(kind: DebugLinkedInSenderKind): string {
  return SENDER_KIND_LABELS[kind];
}

export const THREAD_STATUS_LABELS: Record<
  DebugLinkedInThread['threadStatus'],
  string
> = {
  Active: 'Active',
  Paused: 'Paused',
  ReviewRequired: 'Review required',
};

/**
 * The job a candidate reply is filed under (the job of the last sent message),
 * or the latest job when nothing has been sent yet.
 */
export function replyTargetJob<T extends { isReplyTarget: boolean }>(
  candidateJobs: T[],
): T | undefined {
  return candidateJobs.find((job) => job.isReplyTarget) ?? candidateJobs.at(-1);
}

/** Author of the most recently created message, which is what the AI responder looks at. */
export function lastAuthor(
  messages: Pick<DebugLinkedInMessage, 'author' | 'createdAt'>[],
): DebugLinkedInMessageAuthor | undefined {
  let latest: (typeof messages)[number] | undefined;
  for (const message of messages) {
    if (!latest || message.createdAt > latest.createdAt) latest = message;
  }
  return latest?.author;
}

type AutoReplyThread = Pick<
  DebugLinkedInThread,
  'threadStatus' | 'autoReplyEligibleAt'
> & {
  candidateJobs: Pick<
    DebugLinkedInThread['candidateJobs'][number],
    'isReplyTarget' | 'isMessagingAutomated'
  >[];
};

/**
 * Explains whether the AI responder will answer the candidate's last message.
 * The API computes the batch window end; this adds why no reply is coming,
 * matching BatchMessageResponderWorker's skips for inactive threads and jobs
 * without auto follow-up.
 */
export function autoReplyNote(
  thread: AutoReplyThread,
  lastMessageAuthor: DebugLinkedInMessageAuthor | undefined,
  now: Date,
): string | null {
  if (lastMessageAuthor !== 'Candidate') return null;
  if (thread.threadStatus !== 'Active') {
    return `No auto-reply: thread ${THREAD_STATUS_LABELS[thread.threadStatus].toLowerCase()}`;
  }

  const replyTarget = thread.candidateJobs.find((job) => job.isReplyTarget);
  if (replyTarget && !replyTarget.isMessagingAutomated) {
    return 'No auto-reply: auto follow-up is off for this job';
  }
  if (!thread.autoReplyEligibleAt) return null;

  const eligibleAt = new Date(thread.autoReplyEligibleAt);
  if (eligibleAt > now) {
    const time = eligibleAt.toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
    });
    return `AI reply eligible after ${time} (batch window)`;
  }
  return 'AI reply due, waiting for the next responder run';
}
